/**
 * Music API client — search and download tracks via the VPS backend.
 */

export interface MusicTrack {
  id: string;
  title: string;
  channel: string;
  duration: number | null;
  thumbnail: string | null;
}

export interface TrimOptions {
  start: number;
  duration: number;
}

interface SearchResponse {
  results: MusicTrack[];
}

function readEnv(key: string): string | null {
  try {
    const val = (import.meta as ImportMeta).env[key as keyof ImportMetaEnv];
    return typeof val === 'string' && val.length > 0 ? val : null;
  } catch {
    return null;
  }
}

function apiBase(): string {
  return readEnv('VITE_MUSIC_API_URL') ?? '/api/music';
}

function apiToken(): string | null {
  return readEnv('VITE_MUSIC_API_TOKEN');
}

function authHeaders(): HeadersInit {
  const token = apiToken();
  if (token === null) return {};
  return { Authorization: `Bearer ${token}` };
}

export async function searchMusic(query: string): Promise<MusicTrack[]> {
  const base = apiBase();
  const params = new URLSearchParams({ q: query.trim() });
  const res = await fetch(`${base}/search?${params}`, {
    headers: authHeaders(),
  });

  if (res.status === 429) {
    throw new Error('rate_limit');
  }
  if (res.status === 401) {
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    throw new Error('search_failed');
  }

  const json = (await res.json()) as SearchResponse;
  return json.results;
}

function safeDownloadName(name: string): string {
  const cleaned = name.replace(/[<>:"/\\|?*]/g, '').trim();
  return cleaned.length > 0 ? cleaned : 'track';
}

export function buildDownloadUrl(videoId: string, trim?: TrimOptions): string {
  const base = apiBase();
  const token = apiToken();
  const url = `${base}/download/${encodeURIComponent(videoId)}`;
  const params = new URLSearchParams();
  if (token !== null) {
    params.set('token', token);
  }
  if (trim !== undefined) {
    params.set('trimStart', String(trim.start));
    params.set('trimDuration', String(trim.duration));
  }
  const query = params.toString();
  return query.length > 0 ? `${url}?${query}` : url;
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Trigger a file download in the browser. Uses fetch + blob so we can
 * surface errors; falls back to a direct link if fetch fails.
 */
export async function downloadTrack(
  track: MusicTrack,
  trim?: TrimOptions,
  onProgress?: (downloading: boolean) => void,
): Promise<void> {
  onProgress?.(true);
  try {
    const url = buildDownloadUrl(track.id, trim);
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) {
      throw new Error('download_failed');
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = `${safeDownloadName(track.title)}${trim ? '-kesit' : ''}.mp3`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } finally {
    onProgress?.(false);
  }
}

export async function trimUploadedTrack(
  file: File,
  trim: TrimOptions,
  onProgress?: (trimming: boolean) => void,
): Promise<void> {
  onProgress?.(true);
  try {
    const base = apiBase();
    const formData = new FormData();
    formData.set('file', file);
    formData.set('trimStart', String(trim.start));
    formData.set('trimDuration', String(trim.duration));

    const res = await fetch(`${base}/trim-upload`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    if (!res.ok) {
      throw new Error('upload_trim_failed');
    }

    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    const baseName = file.name.replace(/\.[^.]+$/, '');
    anchor.href = objectUrl;
    anchor.download = `${safeDownloadName(baseName)}-kesit.mp3`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } finally {
    onProgress?.(false);
  }
}
